import type {
  DocumentTypesFile,
  Firm,
  FirmOverrides,
  OverrideGovernance,
  OverrideNumericBound,
  ProductTypesFile,
} from '@/types/bundle'
import type { FieldError } from '@/lib/apiErrors'

export const MASKED_API_KEY_PLACEHOLDER = '********'

// A firm's displayed API-key field value - the masked placeholder whenever
// some form of key already exists, whether still a raw (masked) auth.api_key
// or migrated to auth.api_key_secret_ref (PRD 28) - the frontend never sees
// the real value either way, so both render identically. Genuinely empty
// only for a brand-new/duplicated firm with neither yet (buildNewFirm's own
// `auth.api_key: ''`, never `api_key_secret_ref`).
export function getFirmAuthDisplayValue(firm: Firm): string {
  if (firm.auth.api_key !== undefined) return firm.auth.api_key
  if (firm.auth.api_key_secret_ref) return MASKED_API_KEY_PLACEHOLDER
  return ''
}

export type OverrideBlockName = 'product_overrides' | 'document_type_overrides'

export interface ConcreteOverridableField {
  /** Firm-agnostic target path, e.g. "product_types.<id>.limits.max_documents" -
   * override.py's own canonicalization of a firm's overrides block, and the
   * form allowlist_paths/numeric_bounds/denylist_prefixes patterns match against. */
  canonicalPath: string
  block: OverrideBlockName
  entityId: string
  /** Set when the pattern has a second wildcard (e.g. a document type's field id). */
  fieldId?: string
  /** Path within a firm's `overrides[block][entityId]` record. */
  dottedPath: string
  /** The product/document type's own label, e.g. "Memorandum of Sale Data Extract". */
  entityLabel: string
  /** Just the field part, e.g. "Max Documents", or "Sellers Solicitor Contact
   * Name — Min Confidence" for a document-type field - never repeats entityLabel. */
  fieldLabel: string
  platformDefaultValue: number | string | boolean | undefined
  bound?: OverrideNumericBound
}

// JS port of Python fnmatch semantics (override.py's own matcher): '*'
// matches any run of characters - not path-segment-aware - '?' matches one
// character, everything else is literal. Replicated exactly rather than a
// stricter "one segment per *" assumption, since the real engine's own
// allowlist_paths/numeric_bounds/denylist_prefixes matching relies on it.
function globToRegExp(pattern: string): RegExp {
  let source = '^'
  for (const char of pattern) {
    if (char === '*') source += '.*'
    else if (char === '?') source += '.'
    else source += /[.*+?^${}()|[\]\\]/.test(char) ? `\\${char}` : char
  }
  return new RegExp(`${source}$`)
}

export function matchesGlobPath(pattern: string, path: string): boolean {
  return globToRegExp(pattern).test(path)
}

const ROOT_TO_BLOCK: Record<string, OverrideBlockName> = {
  product_types: 'product_overrides',
  document_types: 'document_type_overrides',
}

interface LeafMatch {
  dottedSegments: string[]
  fieldId?: string
  value: unknown
}

// Generically walks one allowlist_paths glob pattern's remaining segments
// (after the entity-id wildcard) against the real product_types/
// document_types data, substituting every '*' with each real key found at
// that point - driven entirely by the pattern + real data, not a hardcoded
// per-field list (PRD 06 AC3: "not hardcoded per-field in the component").
function collectLeaves(
  node: unknown,
  remaining: string[],
  dottedSoFar: string[],
  fieldId: string | undefined,
): LeafMatch[] {
  if (remaining.length === 0) {
    return [{ dottedSegments: dottedSoFar, fieldId, value: node }]
  }
  if (typeof node !== 'object' || node === null) return []
  const [segment, ...rest] = remaining

  if (segment === '*') {
    return Object.entries(node as Record<string, unknown>).flatMap(([key, child]) =>
      collectLeaves(child, rest, [...dottedSoFar, key], fieldId ?? key),
    )
  }

  const child = (node as Record<string, unknown>)[segment]
  if (child === undefined) return []
  return collectLeaves(child, rest, [...dottedSoFar, segment], fieldId)
}

function humanize(key: string): string {
  return key
    .split('_')
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ')
}

// Exported so EnabledProductsEditor can label real product ids the same way
// (product.description, falling back to a humanized id) without duplicating
// this logic.
export function entityLabel(rootKey: string, entityId: string, productTypes: ProductTypesFile): string {
  if (rootKey === 'product_types') {
    return productTypes.product_types[entityId]?.description ?? humanize(entityId)
  }
  return humanize(entityId)
}

// numeric_bounds is keyed by the same glob-path vocabulary as
// allowlist_paths, matched the same way (override.py's own _check_bounds).
// tightening_only caps the effective max at the platform's own current
// value - confirmed against override.py: a firm may only lower a
// tightening_only field below the live baseline, never raise it, regardless
// of numeric_bounds' own max. Surfacing this (rather than numeric_bounds
// alone) was a deliberate scope addition beyond the PRD's literal text,
// confirmed with the user.
function resolveEffectiveBound(
  canonicalPath: string,
  governance: OverrideGovernance,
  platformDefaultValue: number | string | boolean | undefined,
): OverrideNumericBound | undefined {
  const boundEntry = Object.entries(governance.numeric_bounds).find(([pattern]) =>
    matchesGlobPath(pattern, canonicalPath),
  )
  const bound = boundEntry?.[1]
  const isTighteningOnly = (governance.tightening_only ?? []).some((pattern) =>
    matchesGlobPath(pattern, canonicalPath),
  )

  if (!isTighteningOnly || typeof platformDefaultValue !== 'number') {
    return bound
  }

  const tightenedMax = bound?.max !== undefined ? Math.min(bound.max, platformDefaultValue) : platformDefaultValue
  return { min: bound?.min, max: tightenedMax }
}

// Expands global_defaults.override_governance.allowlist_paths (glob
// patterns like "product_types.*.limits.max_documents" or
// "document_types.*.fields.*.acceptance.min_confidence") into concrete,
// named, per-entity fields - reading through to the real product_types/
// document_types data for both the entity list (what `*` can be) and each
// field's platform-default value. Only "product_types.*.…"/
// "document_types.*.…" shaped patterns are supported (the only two real
// blocks clients.json's overrides can target per override.py's own
// _BLOCK_TO_CANONICAL_PREFIX) - any other pattern shape is skipped rather
// than guessed at.
export function expandAllowlistToConcreteFields(
  governance: OverrideGovernance,
  productTypes: ProductTypesFile,
  documentTypes: DocumentTypesFile,
): ConcreteOverridableField[] {
  const roots: Record<string, Record<string, unknown>> = {
    product_types: productTypes.product_types,
    document_types: documentTypes.document_types,
  }

  const results: ConcreteOverridableField[] = []

  for (const pattern of governance.allowlist_paths) {
    const [rootKey, entityWildcard, ...rest] = pattern.split('.')
    const rootData = roots[rootKey]
    const block = ROOT_TO_BLOCK[rootKey]
    if (!rootData || !block || entityWildcard !== '*' || rest.length === 0) continue

    for (const [entityId, entityNode] of Object.entries(rootData)) {
      for (const leaf of collectLeaves(entityNode, rest, [], undefined)) {
        const dottedPath = leaf.dottedSegments.join('.')
        const canonicalPath = [rootKey, entityId, ...leaf.dottedSegments].join('.')
        const platformDefaultValue = leaf.value as ConcreteOverridableField['platformDefaultValue']
        // Middle dot, not an em dash - matches this same screen's own
        // separator convention (OverrideValueInputCell's "Min 0 · Max 2").
        const fieldLabel = [
          leaf.fieldId ? humanize(leaf.fieldId) : undefined,
          humanize(leaf.dottedSegments[leaf.dottedSegments.length - 1]),
        ]
          .filter(Boolean)
          .join(' · ')

        results.push({
          canonicalPath,
          block,
          entityId,
          fieldId: leaf.fieldId,
          dottedPath,
          entityLabel: entityLabel(rootKey, entityId, productTypes),
          fieldLabel,
          platformDefaultValue,
          bound: resolveEffectiveBound(canonicalPath, governance, platformDefaultValue),
        })
      }
    }
  }

  return results
}

// A document type is "relevant" to a firm if one of the firm's *enabled*
// products actually requires it (hard_floor / rules.must_have_all / a
// flattened rules.must_have_any_of) - real fields confirmed on
// ProductType.document_requirements. Used to scope which document-type
// override fields are offered for a given firm, symmetric with how
// product-limit fields are scoped by firm.enabled_products directly.
export function relevantDocumentTypeIds(productTypes: ProductTypesFile, enabledProductIds: string[]): Set<string> {
  const ids = new Set<string>()
  for (const productId of enabledProductIds) {
    const requirements = productTypes.product_types[productId]?.document_requirements
    if (!requirements) continue
    for (const id of requirements.hard_floor ?? []) ids.add(id)
    for (const id of requirements.rules?.must_have_all ?? []) ids.add(id)
    for (const group of requirements.rules?.must_have_any_of ?? []) {
      for (const id of group) ids.add(id)
    }
  }
  return ids
}

// Narrows the full, governance-expanded field list down to what actually
// applies to one firm - a product's fields only if that product is in
// firm.enabled_products, a document type's fields only if
// relevantDocumentTypeIds says one of the firm's enabled products needs it.
// Only used to decide what's offered to *add* (Add Override popup) - a
// firm's already-stored overrides stay visible/removable in the table
// regardless, even if the underlying product was disabled afterward.
export function filterFieldsForFirm(
  fields: ConcreteOverridableField[],
  firm: Firm,
  productTypes: ProductTypesFile,
): ConcreteOverridableField[] {
  const enabledProductIds = new Set(firm.enabled_products)
  const docTypeIds = relevantDocumentTypeIds(productTypes, firm.enabled_products)
  return fields.filter((field) =>
    field.block === 'product_overrides' ? enabledProductIds.has(field.entityId) : docTypeIds.has(field.entityId),
  )
}

function ensureOverridesBlock(
  overrides: FirmOverrides | undefined,
  block: OverrideBlockName,
  entityId: string,
): FirmOverrides {
  const nextOverrides: FirmOverrides = { ...overrides }
  const nextBlock = { ...(nextOverrides[block] ?? {}) }
  nextBlock[entityId] = { ...(nextBlock[entityId] ?? {}) }
  nextOverrides[block] = nextBlock
  return nextOverrides
}

export function readOverrideValue(
  firm: Firm,
  field: ConcreteOverridableField,
): number | string | boolean | undefined {
  return firm.overrides?.[field.block]?.[field.entityId]?.[field.dottedPath]
}

export function setOverrideValue(firm: Firm, field: ConcreteOverridableField, value: number): Firm {
  const nextOverrides = ensureOverridesBlock(firm.overrides, field.block, field.entityId)
  nextOverrides[field.block]![field.entityId] = {
    ...nextOverrides[field.block]![field.entityId],
    [field.dottedPath]: value,
  }
  return { ...firm, overrides: nextOverrides }
}

// Symmetric with setOverrideValue - reverts a field back to the platform
// default by removing its entry from the firm's overrides storage entirely
// (not just setting it back to the default value, which would still count
// as a stored override). Not in the PRD's own Interactions text (only "+ Add
// Override" is described there); added after confirming with the user that
// an add-only screen with no way back would be a real gap.
//
// Real incident, fixed: this used to stop cleaning up at the per-entity
// level - removing the last override for an entity deleted that entity key,
// but left the whole block (e.g. document_type_overrides) behind as an empty
// object, and left firm.overrides itself behind too if every block was now
// empty. A firm whose *live* clients.json never had that key at all would
// then end up with a draft carrying `overrides: { document_type_overrides:
// {} }` - an empty container present where live has the key fully absent, a
// genuine (backend-confirmed correct, not spurious) diff that made an
// add-then-revert look like a real, unpublishable change forever. Cascades
// the cleanup all the way up now, symmetric with how ensureOverridesBlock
// builds it back down - reverting the last override round-trips back to
// exactly the shape a never-touched firm has.
export function removeOverrideValue(firm: Firm, field: ConcreteOverridableField): Firm {
  const entity = firm.overrides?.[field.block]?.[field.entityId]
  if (!entity) return firm

  const nextEntity = { ...entity }
  delete nextEntity[field.dottedPath]

  const nextBlock = { ...firm.overrides![field.block] }
  if (Object.keys(nextEntity).length === 0) {
    delete nextBlock[field.entityId]
  } else {
    nextBlock[field.entityId] = nextEntity
  }

  const nextOverrides = { ...firm.overrides }
  if (Object.keys(nextBlock).length === 0) {
    delete nextOverrides[field.block]
  } else {
    nextOverrides[field.block] = nextBlock
  }

  return {
    ...firm,
    overrides: Object.keys(nextOverrides).length === 0 ? undefined : nextOverrides,
  }
}

// Uppercase, non [A-Za-z0-9_-] -> '_', collapsed, FIRM_-prefixed (matches
// every real firm key seen in legalflow-idp's own bundle fixtures, e.g.
// FIRM_CONVEYANCINGHOME) - not enforced by the schema itself (any string
// matching override.py's own path-segment charset is valid), but consistent
// with existing convention. Deduped against existingIds with a numeric
// suffix on collision.
export function deriveFirmId(name: string, existingIds: string[]): string {
  const slug = name
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9_-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
  const base = slug.length === 0 ? 'FIRM' : slug.startsWith('FIRM_') ? slug : `FIRM_${slug}`

  if (!existingIds.includes(base)) return base
  let suffix = 2
  while (existingIds.includes(`${base}_${suffix}`)) suffix += 1
  return `${base}_${suffix}`
}

// Core logic steps 2-4 of Frontend PRD 06: enabled_products/overrides/
// output_profile_id/usage_plan copy from `source` (present for Duplicate,
// absent for a brand-new "+ Add Firm"); auth.api_key is always genuinely
// empty (never the masked placeholder - there is no prior real key for
// either case, on the frontend or the backend); enabled always starts false
// regardless of the source firm's own state. usage_plan isn't one of PRD
// 06's two hard-security exceptions (api_key, enabled), so it's copied like
// every other config field - a duplicated firm reasonably starts with the
// same quota shape as its source.
export function buildNewFirm(name: string, source?: Firm): Firm {
  return {
    name,
    enabled: false,
    enabled_products: source ? [...source.enabled_products] : [],
    auth: { scheme: 'api_key', api_key: '' },
    overrides: source?.overrides ? structuredClone(source.overrides) : undefined,
    output_profile_id: source?.output_profile_id,
    usage_plan: source?.usage_plan ? structuredClone(source.usage_plan) : undefined,
  }
}

// The exact `path` format the backend emits for a *governance* (allowlist/
// denylist/bounds/tightening) rejection on a clients.json PUT specifically
// is undocumented anywhere found in legalflow-idp - only the raw-schema
// error path convention (a dotted pointer into the PUT's own content, e.g.
// "firms.<id>.auth.api_key") is confirmed. This matches a failed error
// against both plausible shapes: the override's canonical target path (the
// form override.py's own violation messages use) and the raw clients.json
// storage path - a real server response routes to the right row either way,
// and anything matching neither still surfaces via the top-level
// Save-failed banner rather than being silently dropped. Known limitation:
// since the canonical path has no firm identifier in it, two different
// firms overriding the same field could both match one firm's violation -
// narrow edge case, disclosed rather than silently accepted as fully solved.
export function matchFieldError(error: FieldError, firmId: string, field: ConcreteOverridableField): boolean {
  const storagePath = `firms.${firmId}.overrides.${field.block}.${field.entityId}.${field.dottedPath}`
  return error.path === field.canonicalPath || error.path === storagePath
}
